import { GET_COMPANY } from "@apiUtils";
import type { IPagedResponse } from "@atlas/sdk";
import { mutationFetch } from "@atlas/sdk";
import type { IActiveSubscription } from "@models";
import {
  type InfiniteData,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const ACTIVE_SUBSCRIPTIONS_INFINITE_KEY =
  "activeSubscriptionsInfiniteKey";

export const SUBSCRIPTION_ENTITLEMENTS_KEY = "subscriptionEntitlementsKey";

export interface IActiveSubscriptionsRequest {
  companyUuid: string;
  page?: number;
  size?: number;
}

export interface ISubscriptionEntitlement {
  id: string;
  subsPlanEntitlementId: string;
  isMandatory: boolean;
  isEditable: boolean;
  name: string;
  entitlementCode: string;
  entitlementType: string;
  valueDefinition?: string;
  unitValue?: string;
  units?: string;
  unitType?: string;
  minimumUnitValue?: string;
  isActive: boolean;
}

export interface ISubscriptionEntitlementCategory {
  id: string;
  categoryName: string;
  categoryCode: string;
  entitlements: ISubscriptionEntitlement[];
}

export const getActiveSubscriptionsInfiniteKey = (
  request: IActiveSubscriptionsRequest,
  basePath?: string,
) => [ACTIVE_SUBSCRIPTIONS_INFINITE_KEY, request, basePath] as const;

export const getSubscriptionEntitlementsKey = (
  subscriptionId: string,
  basePath?: string,
) => [SUBSCRIPTION_ENTITLEMENTS_KEY, subscriptionId, basePath] as const;

export const hubSubscriptionEntitlements = async (
  subscriptionId: string,
  basePath?: string,
): Promise<ISubscriptionEntitlementCategory[]> => {
  const path = `${GET_COMPANY}/active-subscriptions/${subscriptionId}/entitlements`;
  const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
  return mutationFetch<ISubscriptionEntitlementCategory[]>({
    url,
    method: "GET",
  });
};

export const useSubscriptionEntitlements = (
  subscriptionId: string | undefined,
  basePath?: string,
  options?: Partial<
    UseQueryOptions<
      ISubscriptionEntitlementCategory[],
      AxiosError,
      ISubscriptionEntitlementCategory[],
      ReturnType<typeof getSubscriptionEntitlementsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getSubscriptionEntitlementsKey(subscriptionId ?? "", basePath),
    queryFn: () => hubSubscriptionEntitlements(subscriptionId ?? "", basePath),
    enabled: !!subscriptionId,
    ...options,
  });
};

export const useActiveSubscriptionsInfinite = (
  companyUuid: string | undefined,
  basePath?: string,
  params?: { size?: number; page?: number },
  options?: Partial<
    UseInfiniteQueryOptions<
      IPagedResponse<IActiveSubscription>, // 1. TQueryFnData
      AxiosError, // 2. TError
      InfiniteData<IPagedResponse<IActiveSubscription>>, // 3. TData
      ReturnType<typeof getActiveSubscriptionsInfiniteKey>, // 4. TQueryKey
      number // 5. TPageParam
    >
  >,
) => {
  const size = params?.size ?? 10;
  const page = params?.page ?? 0;

  const initialRequest: IActiveSubscriptionsRequest = {
    companyUuid: companyUuid ?? "",
    page,
    size,
  };

  return useInfiniteQuery({
    queryKey: getActiveSubscriptionsInfiniteKey(initialRequest, basePath),
    queryFn: async ({ pageParam }) => {
      const path = `${GET_COMPANY}/${companyUuid}/active-subscriptions`;
      const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;

      return mutationFetch<IPagedResponse<IActiveSubscription>>({
        url,
        method: "GET",
        params: { page: pageParam, size },
      });
    },
    initialPageParam: page,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.number + 1;
      return nextPage < lastPage.totalPages ? nextPage : undefined;
    },
    getPreviousPageParam: (firstPage) =>
      firstPage.number > 0 ? firstPage.number - 1 : undefined,
    enabled: !!companyUuid,
    ...options,
  });
};
